import { displayScalar, humanizeKey } from "@/lib/record-presentation"
import type { TranslationKey, TranslationVariables } from "@/lib/translations"
import { isRecord } from "@/lib/utils"
import type { AtsFieldDescriptor } from "@/types/ats-explorer-api"

/** The active language's translator, passed in because this module has no hooks */
type Translate = (key: TranslationKey, variables?: TranslationVariables) => string

/**
 * The explorer grid's column model. Columns come from the ordered field selection, not
 * from the shape of the first record, so every selected path is a column even when the
 * vendor returns it nested (Bullhorn: `jobOrder.title` arrives as `{ jobOrder: { title } }`).
 */

export interface ExplorerColumn {
  /** Selected path in field notation: "status", "jobOrder.title", "commissions[].id" */
  path: string
  /** Header text: the vendor label when metadata declares one, else the humanised last segment */
  label: string
  /** The parent path, humanised, for a nested field ("Job order"); null at the top level */
  detail: string | null
  /** Whether the ATS can order the list by this field */
  sortable: boolean
}

export interface ExplorerSort {
  path: string
  descending: boolean
}

const LEGACY_SUMMARY_COLUMNS = ["id", "name", "title", "email", "status", "created_at", "published_name"]
const LEGACY_COLUMN_CAP = 6

export function buildColumns(selection: readonly string[], schema: readonly AtsFieldDescriptor[] | null, sortableFields: readonly string[]): ExplorerColumn[] {
  const labels = new Map((schema ?? []).map((field) => [field.path, field.label ?? null]))
  const sortable = new Set(sortableFields)
  return selection.map((path) => {
    const segments = path.split(".")
    const leaf = segments[segments.length - 1] ?? path
    const parent = segments.slice(0, -1).join(".")
    const label = labels.get(path)
    return {
      path,
      label: label && label !== path ? label : humanizeKey(leaf),
      detail: parent === "" ? null : humanizeKey(parent.replace(/\[\]/g, "")),
      sortable: sortable.has(path),
    }
  })
}

/**
 * The columns to show before the user has chosen any: the summary keys present on the first
 * record, then a few more scalar keys. They sort like chosen columns do, because whether the
 * ATS can order by a field does not depend on who picked it.
 */
export function fallbackColumns(records: readonly Record<string, unknown>[], sortableFields: readonly string[] = []): ExplorerColumn[] {
  const first = records[0]
  if (!first) return []
  const sortable = new Set(sortableFields)
  const available = Object.keys(first)
  const preferred = LEGACY_SUMMARY_COLUMNS.filter((key) => available.includes(key))
  const extras = available
    .filter((key) => !preferred.includes(key) && typeof first[key] !== "object")
    .slice(0, Math.max(0, LEGACY_COLUMN_CAP - preferred.length))
  return [...preferred, ...extras].map((key) => ({ path: key, label: humanizeKey(key), detail: null, sortable: sortable.has(key) }))
}

/**
 * One line of cell text for any value shape the vendors return. A scalar is formatted from
 * its path, the same way the record detail does, so a date reads as a date in both.
 */
export function formatColumnValue(path: string, value: unknown, t: Translate): string {
  if (value === null || value === undefined) return ""
  if (Array.isArray(value)) return value.map((item) => formatColumnValue(path, item, t)).filter((text) => text !== "").join(", ")
  if (isRecord(value)) {
    if (Array.isArray(value.data)) {
      const count = typeof value.total === "number" ? value.total : value.data.length
      return t((count === 1 ? "explorer.record.itemsOne" : "explorer.record.itemsOther"), { count })
    }
    const handle = ["name", "title", "label"].map((key) => value[key]).find((candidate) => typeof candidate === "string" && candidate !== "")
    if (typeof handle === "string") return "id" in value ? `${handle} · #${String(value.id)}` : handle
    if ("id" in value) return `#${String(value.id)}`
    return "{…}"
  }
  return displayScalar(path, value, t)
}

/** Header click cycle: none → ascending → descending → none */
export function nextSort(current: ExplorerSort | null, path: string): ExplorerSort | null {
  if (current?.path !== path) return { path, descending: false }
  return current.descending ? null : { path, descending: true }
}
